import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Plus, Save, Trash2, Undo2, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { MarkdownEditor } from '../common/MarkdownEditor';
import {
  useBrandApi,
  useCampaignApi,
  type BrandDetail,
  type BrandSummary,
  type CampaignReferenceData,
  type TargetProfileOption
} from '../../services/apiService';
import { type NavigationItem } from '../Navigation';

type StringArrayKeys =
  | 'values'
  | 'approvedPhrases'
  | 'disclaimers'
  | 'culturalSensitivities'
  | 'markets'
  | 'languages'
  | 'topics'
  | 'hashtags';

interface BrandManagementProps {
  onNavigate: (view: NavigationItem) => void;
}

const emptyBrandDetail: BrandDetail = {
  id: null,
  name: '',
  tagline: '',
  promise: '',
  valueProposition: '',
  logoUrl: '',
  nameVariants: [],
  values: [],
  differentiators: [],
  tone: {
    archetype: '',
    styleNotes: '',
    doKeywords: [],
    dontKeywords: [],
    examples: []
  },
  pillars: [],
  approvedPhrases: [],
  bannedPhrases: [],
  storylines: [],
  disclaimers: [],
  claims: [],
  culturalSensitivities: [],
  audiences: [],
  markets: [],
  languages: [],
  topics: [],
  hashtags: [],
  channels: []
};

const normalizeBrandDetail = (detail: BrandDetail | null | undefined): BrandDetail => {
  const normalized: BrandDetail = {
    ...emptyBrandDetail,
    ...detail,
    tagline: detail?.tagline ?? '',
    promise: detail?.promise ?? '',
    valueProposition: detail?.valueProposition ?? '',
    logoUrl: detail?.logoUrl ?? '',
    nameVariants:
      detail?.nameVariants?.map(variant => ({
        locale: variant.locale,
        shortName: variant.shortName,
        longName: variant.longName ?? '',
        phonetic: variant.phonetic ?? ''
      })) ?? [],
    values: detail?.values ?? [],
    differentiators:
      detail?.differentiators?.map(d => ({
        id: d.id,
        text: d.text,
        order: d.order ?? 1
      })) ?? [],
    tone: {
      archetype: detail?.tone?.archetype ?? '',
      styleNotes: detail?.tone?.styleNotes ?? '',
      doKeywords: detail?.tone?.doKeywords ?? [],
      dontKeywords: detail?.tone?.dontKeywords ?? [],
      examples:
        detail?.tone?.examples?.map(example => ({
          context: example.context ?? '',
          exampleText: example.exampleText,
          order: example.order ?? 1
        })) ?? []
    },
    pillars:
      detail?.pillars?.map(pillar => ({
        id: pillar.id,
        name: pillar.name,
        priority: pillar.priority ?? null,
        messages:
          pillar.messages?.map(message => ({
            id: message.id,
            message: message.message,
            order: message.order ?? 1
          })) ?? []
      })) ?? [],
    approvedPhrases: detail?.approvedPhrases ?? [],
    bannedPhrases:
      detail?.bannedPhrases?.map(phrase => ({
        id: phrase.id,
        phrase: phrase.phrase,
        reason: phrase.reason ?? ''
      })) ?? [],
    storylines:
      detail?.storylines?.map(storyline => ({
        id: storyline.id,
        title: storyline.title,
        order: storyline.order ?? 1
      })) ?? [],
    disclaimers: detail?.disclaimers ?? [],
    claims:
      detail?.claims?.map(claim => ({
        id: claim.id,
        text: claim.text,
        approved: claim.approved,
        expiryDate: claim.expiryDate ?? null,
        jurisdictions: claim.jurisdictions ?? []
      })) ?? [],
    culturalSensitivities: detail?.culturalSensitivities ?? [],
    audiences:
      detail?.audiences?.map(audience => ({
        id: audience.id,
        targetProfileOptionId: audience.targetProfileOptionId,
        description: audience.description ?? '',
        valueDrivers: audience.valueDrivers ?? [],
        objections: audience.objections ?? []
      })) ?? [],
    markets: detail?.markets ?? [],
    languages: detail?.languages ?? [],
    topics: detail?.topics ?? [],
    hashtags: detail?.hashtags ?? [],
    channels:
      detail?.channels?.map(channel => ({
        id: channel.id,
        name: channel.name,
        toneOverride: channel.toneOverride ?? '',
        lengthGuidance: channel.lengthGuidance ?? '',
        callToActions: channel.callToActions ?? []
      })) ?? []
  };

  return normalized;
};

const flattenTargetProfiles = (profiles: TargetProfileOption[]): TargetProfileOption[] => {
  const queue = [...profiles];
  const flattened: TargetProfileOption[] = [];
  while (queue.length > 0) {
    const option = queue.shift();
    if (!option) {
      continue;
    }
    flattened.push(option);
    if (option.children?.length) {
      queue.push(...option.children);
    }
  }
  return flattened;
};

export default function BrandManagement({ onNavigate }: BrandManagementProps) {
  const { getBrands, getBrand, createBrand, updateBrand, deleteBrand } = useBrandApi();
  const campaignApi = useCampaignApi();

  const [brandList, setBrandList] = useState<BrandSummary[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [brandDetail, setBrandDetail] = useState<BrandDetail>(emptyBrandDetail);
  const [originalBrandDetail, setOriginalBrandDetail] = useState<BrandDetail>(emptyBrandDetail); // last saved snapshot
  const [referenceData, setReferenceData] = useState<CampaignReferenceData | null>(null);

  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const isNew = selectedBrandId === null;
  const isDirty = useMemo(() => {
    // Compare normalized JSON snapshots
    return JSON.stringify(normalizeBrandDetail(brandDetail)) !== JSON.stringify(normalizeBrandDetail(originalBrandDetail));
  }, [brandDetail, originalBrandDetail]);

  useEffect(() => {
    const loadReference = async () => {
      try {
        const data = await campaignApi.getReferenceData();
        setReferenceData(data);
      } catch (err) {
        console.error('Failed to load campaign reference data', err);
      }
    };

    loadReference();
  }, [campaignApi]);

  useEffect(() => {
    const loadBrands = async () => {
      setListLoading(true);
      try {
        const brands = await getBrands();
        const sorted = brands.slice().sort((a, b) => a.name.localeCompare(b.name));
        setBrandList(sorted);
        if (sorted.length > 0) {
          setSelectedBrandId(sorted[0].id);
        }
      } catch (err) {
        console.error('Failed to load brands', err);
        setError('Failed to load brands. Please try again.');
      } finally {
        setListLoading(false);
      }
    };

    loadBrands();
  }, [getBrands]);

  useEffect(() => {
    const loadDetail = async () => {
      if (selectedBrandId === null) {
        setBrandDetail(emptyBrandDetail);
        setOriginalBrandDetail(emptyBrandDetail);
        return;
      }

      setDetailLoading(true);
      setError(null);
      try {
        const detail = await getBrand(selectedBrandId);
        const normalized = normalizeBrandDetail(detail);
        setBrandDetail(normalized);
        setOriginalBrandDetail(normalized);
      } catch (err) {
        console.error('Failed to load brand detail', err);
        setError('Failed to load brand details. Please try again.');
      } finally {
        setDetailLoading(false);
      }
    };

    loadDetail();
  }, [getBrand, selectedBrandId]);

  const allTargetProfiles = useMemo(() => {
    if (!referenceData) {
      return [] as TargetProfileOption[];
    }
    return [
      ...flattenTargetProfiles(referenceData.b2bTargetProfiles ?? []),
      ...flattenTargetProfiles(referenceData.b2cTargetProfiles ?? [])
    ];
  }, [referenceData]);

  const filteredBrands = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return brandList;
    return brandList.filter(b => b.name.toLowerCase().includes(term) || (b.tagline?.toLowerCase().includes(term) ?? false));
  }, [brandList, searchTerm]);

  const updateStringArrayField = (key: StringArrayKeys, index: number, value: string) => {
    setBrandDetail(prev => {
      const list = [...(prev[key] as string[])];
      list[index] = value;
      return { ...prev, [key]: list };
    });
  };

  const addStringToField = (key: StringArrayKeys) => {
    setBrandDetail(prev => ({
      ...prev,
      [key]: [...(prev[key] as string[]), '']
    }));
  };

  const removeStringFromField = (key: StringArrayKeys, index: number) => {
    setBrandDetail(prev => {
      const list = [...(prev[key] as string[])];
      list.splice(index, 1);
      return { ...prev, [key]: list };
    });
  };

  const handleSelectBrand = (brandId: number) => {
    if (isDirty) {
      const proceed = window.confirm('You have unsaved changes. Discard and switch brands?');
      if (!proceed) return;
    }
    setSelectedBrandId(brandId);
    setSuccessMessage(null);
  };

  const handleCreateBrand = () => {
    if (isDirty && !isNew) {
      const proceed = window.confirm('You have unsaved changes. Discard and create a new brand?');
      if (!proceed) return;
    }
    setSelectedBrandId(null);
    setBrandDetail(emptyBrandDetail);
    setOriginalBrandDetail(emptyBrandDetail);
    setSuccessMessage(null);
  };

  const handleCancelChanges = () => {
    if (!isDirty) return;
    const confirmRevert = window.confirm('Discard all unsaved changes?');
    if (!confirmRevert) return;
    setBrandDetail(originalBrandDetail);
    setSuccessMessage(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!brandDetail.name.trim()) {
      setError('Brand name is required.');
      return;
    }
    if (!isDirty && !isNew) {
      setSuccessMessage('No changes to save.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = normalizeBrandDetail(brandDetail);
      const sanitize = (items: string[]) => items.map(item => item.trim()).filter(item => item.length > 0);

      payload.values = sanitize(payload.values);
      payload.approvedPhrases = sanitize(payload.approvedPhrases);
      payload.disclaimers = payload.disclaimers.map(item => item.trim()).filter(item => item.length > 0);
      payload.culturalSensitivities = sanitize(payload.culturalSensitivities);
      payload.markets = sanitize(payload.markets);
      payload.languages = sanitize(payload.languages);
      payload.topics = sanitize(payload.topics);
      payload.hashtags = sanitize(payload.hashtags);
      payload.bannedPhrases = payload.bannedPhrases
        .map(phrase => ({ ...phrase, phrase: phrase.phrase.trim(), reason: phrase.reason?.trim() ?? '' }))
        .filter(phrase => phrase.phrase.length > 0);
      payload.storylines = payload.storylines.filter(storyline => storyline.title.trim().length > 0);
      payload.differentiators = payload.differentiators.filter(d => d.text.trim().length > 0);
      payload.claims = payload.claims.map(claim => ({
        ...claim,
        text: claim.text.trim(),
        jurisdictions: sanitize(claim.jurisdictions)
      })).filter(claim => claim.text.length > 0);
      payload.audiences = payload.audiences.filter(audience => audience.targetProfileOptionId > 0);
      payload.channels = payload.channels.map(channel => ({
        ...channel,
        name: channel.name.trim(),
        toneOverride: channel.toneOverride?.trim() ?? '',
        lengthGuidance: channel.lengthGuidance?.trim() ?? '',
        callToActions: sanitize(channel.callToActions)
      })).filter(channel => channel.name.length > 0);

      let saved: BrandDetail;
      if (selectedBrandId) {
        saved = await updateBrand(selectedBrandId, payload);
      } else {
        saved = await createBrand(payload);
      }

      const brands = await getBrands();
      const sorted = brands.slice().sort((a, b) => a.name.localeCompare(b.name));
      setBrandList(sorted);
      const normalizedSaved = normalizeBrandDetail(saved);
      setBrandDetail(normalizedSaved);
      setOriginalBrandDetail(normalizedSaved);
      setSelectedBrandId(saved.id ?? null);
      setSuccessMessage(isNew ? 'Brand created successfully.' : 'Brand changes saved.');
    } catch (err) {
      console.error('Failed to save brand', err);
      setError('Failed to save brand. Please review your inputs and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBrandId) {
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this brand? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      await deleteBrand(selectedBrandId);
      const brands = await getBrands();
      const sorted = brands.slice().sort((a, b) => a.name.localeCompare(b.name));
      setBrandList(sorted);
      if (sorted.length > 0) {
        setSelectedBrandId(sorted[0].id);
      } else {
        setSelectedBrandId(null);
        setBrandDetail(emptyBrandDetail);
        setOriginalBrandDetail(emptyBrandDetail);
      }
      setSuccessMessage('Brand deleted successfully.');
    } catch (err) {
      console.error('Failed to delete brand', err);
      setError('Failed to delete brand. Please try again later.');
    }
  };

  const handleDifferentiatorChange = (index: number, field: 'text' | 'order', value: string) => {
    setBrandDetail(prev => {
      const differentiators = [...prev.differentiators];
      const existing = differentiators[index];
      differentiators[index] = {
        ...existing,
        [field]: field === 'order' ? Number(value) : value
      };
      return { ...prev, differentiators };
    });
  };

  const handlePillarChange = (index: number, field: 'name' | 'priority', value: string) => {
    setBrandDetail(prev => {
      const pillars = [...prev.pillars];
      const existing = pillars[index];
      pillars[index] = {
        ...existing,
        [field]: field === 'priority' ? (value ? Number(value) : null) : value
      };
      return { ...prev, pillars };
    });
  };

  const handlePillarMessageChange = (pillarIndex: number, messageIndex: number, field: 'message' | 'order', value: string) => {
    setBrandDetail(prev => {
      const pillars = [...prev.pillars];
      const pillar = pillars[pillarIndex];
      const messages = [...pillar.messages];
      messages[messageIndex] = {
        ...messages[messageIndex],
        [field]: field === 'order' ? Number(value) : value
      };
      pillars[pillarIndex] = { ...pillar, messages };
      return { ...prev, pillars };
    });
  };

  const handleToneKeywordChange = (type: 'doKeywords' | 'dontKeywords', index: number, value: string) => {
    setBrandDetail(prev => {
      const tone = { ...prev.tone };
      const list = [...tone[type]];
      list[index] = value;
      tone[type] = list;
      return { ...prev, tone };
    });
  };

  const addToneKeyword = (type: 'doKeywords' | 'dontKeywords') => {
    setBrandDetail(prev => ({
      ...prev,
      tone: {
        ...prev.tone,
        [type]: [...prev.tone[type], '']
      }
    }));
  };

  const removeToneKeyword = (type: 'doKeywords' | 'dontKeywords', index: number) => {
    setBrandDetail(prev => {
      const tone = { ...prev.tone };
      const list = [...tone[type]];
      list.splice(index, 1);
      tone[type] = list;
      return { ...prev, tone };
    });
  };

  const handleToneExampleChange = (index: number, field: 'context' | 'exampleText' | 'order', value: string) => {
    setBrandDetail(prev => {
      const examples = [...prev.tone.examples];
      examples[index] = {
        ...examples[index],
        [field]: field === 'order' ? Number(value) : value
      };
      return {
        ...prev,
        tone: {
          ...prev.tone,
          examples
        }
      };
    });
  };

  const handleBannedPhraseChange = (index: number, field: 'phrase' | 'reason', value: string) => {
    setBrandDetail(prev => {
      const bannedPhrases = [...prev.bannedPhrases];
      bannedPhrases[index] = {
        ...bannedPhrases[index],
        [field]: value
      };
      return { ...prev, bannedPhrases };
    });
  };

  const handleStorylineChange = (index: number, field: 'title' | 'order', value: string) => {
    setBrandDetail(prev => {
      const storylines = [...prev.storylines];
      storylines[index] = {
        ...storylines[index],
        [field]: field === 'order' ? Number(value) : value
      };
      return { ...prev, storylines };
    });
  };

  const handleClaimChange = (index: number, field: 'text' | 'approved' | 'expiryDate', value: string | boolean) => {
    setBrandDetail(prev => {
      const claims = [...prev.claims];
      const claim = claims[index];
      claims[index] = {
        ...claim,
        [field]: field === 'approved' ? Boolean(value) : value || null
      };
      return { ...prev, claims };
    });
  };

  const handleClaimJurisdictionChange = (index: number, jurisdictionIndex: number, value: string) => {
    setBrandDetail(prev => {
      const claims = [...prev.claims];
      const claim = claims[index];
      const jurisdictions = [...claim.jurisdictions];
      jurisdictions[jurisdictionIndex] = value;
      claims[index] = { ...claim, jurisdictions };
      return { ...prev, claims };
    });
  };

  const addClaimJurisdiction = (index: number) => {
    setBrandDetail(prev => {
      const claims = [...prev.claims];
      const claim = claims[index];
      claims[index] = { ...claim, jurisdictions: [...claim.jurisdictions, ''] };
      return { ...prev, claims };
    });
  };

  const removeClaimJurisdiction = (index: number, jurisdictionIndex: number) => {
    setBrandDetail(prev => {
      const claims = [...prev.claims];
      const claim = claims[index];
      const jurisdictions = [...claim.jurisdictions];
      jurisdictions.splice(jurisdictionIndex, 1);
      claims[index] = { ...claim, jurisdictions };
      return { ...prev, claims };
    });
  };

  const handleAudienceChange = (index: number, field: 'targetProfileOptionId' | 'description', value: string) => {
    setBrandDetail(prev => {
      const audiences = [...prev.audiences];
      audiences[index] = {
        ...audiences[index],
        [field]: field === 'targetProfileOptionId' ? Number(value) : value
      };
      return { ...prev, audiences };
    });
  };

  const updateAudienceStringList = (index: number, list: 'valueDrivers' | 'objections', itemIndex: number, value: string) => {
    setBrandDetail(prev => {
      const audiences = [...prev.audiences];
      const audience = audiences[index];
      const entries = [...audience[list]];
      entries[itemIndex] = value;
      audiences[index] = { ...audience, [list]: entries };
      return { ...prev, audiences };
    });
  };

  const addAudienceEntry = (index: number, list: 'valueDrivers' | 'objections') => {
    setBrandDetail(prev => {
      const audiences = [...prev.audiences];
      const audience = audiences[index];
      audiences[index] = { ...audience, [list]: [...audience[list], ''] };
      return { ...prev, audiences };
    });
  };

  const removeAudienceEntry = (index: number, list: 'valueDrivers' | 'objections', itemIndex: number) => {
    setBrandDetail(prev => {
      const audiences = [...prev.audiences];
      const audience = audiences[index];
      const entries = [...audience[list]];
      entries.splice(itemIndex, 1);
      audiences[index] = { ...audience, [list]: entries };
      return { ...prev, audiences };
    });
  };

  const handleChannelChange = (index: number, field: 'name' | 'toneOverride' | 'lengthGuidance', value: string) => {
    setBrandDetail(prev => {
      const channels = [...prev.channels];
      channels[index] = {
        ...channels[index],
        [field]: value
      };
      return { ...prev, channels };
    });
  };

  const updateChannelCta = (index: number, ctaIndex: number, value: string) => {
    setBrandDetail(prev => {
      const channels = [...prev.channels];
      const channel = channels[index];
      const callToActions = [...channel.callToActions];
      callToActions[ctaIndex] = value;
      channels[index] = { ...channel, callToActions };
      return { ...prev, channels };
    });
  };

  const addChannelCta = (index: number) => {
    setBrandDetail(prev => {
      const channels = [...prev.channels];
      const channel = channels[index];
      channels[index] = { ...channel, callToActions: [...channel.callToActions, ''] };
      return { ...prev, channels };
    });
  };

  const removeChannelCta = (index: number, ctaIndex: number) => {
    setBrandDetail(prev => {
      const channels = [...prev.channels];
      const channel = channels[index];
      const callToActions = [...channel.callToActions];
      callToActions.splice(ctaIndex, 1);
      channels[index] = { ...channel, callToActions };
      return { ...prev, channels };
    });
  };
  return (
    <div className="min-h-screen bg-[#f8f5f0] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="flex items-center gap-2" onClick={() => onNavigate('campaign-dashboard')}>
              <ArrowLeft className="w-4 h-4" />
              Back to Campaigns
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                {isNew ? 'Create New Brand' : 'Brand Management'}
                {isDirty && <span className="ml-1 text-xs rounded bg-amber-100 text-amber-700 px-2 py-0.5">Unsaved</span>}
              </h1>
              <p className="text-gray-600">
                {isNew ? 'Fill in the details and click Create Brand to add it.' : 'Manage brand metadata, tone, audiences, and guardrails.'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isDirty && (
              <Button variant="outline" onClick={handleCancelChanges} disabled={saving} title="Discard all unsaved edits">
                <Undo2 className="w-4 h-4 mr-2" /> Revert
              </Button>
            )}
            {selectedBrandId && !isNew && (
              <CapabilityButton capability={P.CampaignBrandDelete} variant="destructive" onClick={handleDelete} disabled={saving} title="Delete this brand">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </CapabilityButton>
            )}
            <CapabilityButton capability={P.CampaignBrandUpdate}
              onClick={handleSave}
              disabled={saving || (!isNew && !isDirty) || !brandDetail.name.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              title={(!isNew && !isDirty) ? 'No changes to save' : undefined}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> {isNew ? 'Create Brand' : 'Save Changes'}
                </>
              )}
            </CapabilityButton>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">{successMessage}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          <Card className="h-full">
            <CardHeader className="flex flex-col gap-4">
              <div className="flex items-center justify-between w-full">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">Brands</CardTitle>
                  <CardDescription className="text-xs md:text-sm">Select or search a brand to edit.</CardDescription>
                </div>
                <CapabilityButton capability={P.CampaignBrandCreate} variant="outline" size="sm" onClick={handleCreateBrand} title="Add new brand">
                  <Plus className="w-4 h-4 mr-1" /> New
                </CapabilityButton>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search brands..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="text-xs text-gray-500">{filteredBrands.length} / {brandList.length} brands</div>
            </CardHeader>
            <CardContent>
              {listLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Loading brands...
                </div>
              ) : brandList.length === 0 ? (
                <div className="text-sm text-gray-600">
                  No brands available. Click New to add your first brand.
                </div>
              ) : filteredBrands.length === 0 ? (
                <div className="text-sm text-gray-600">No matches for "{searchTerm}".</div>
              ) : (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                  {filteredBrands.map(brand => (
                    <Button
                      key={brand.id}
                      variant={selectedBrandId === brand.id ? 'secondary' : 'ghost'}
                      className="w-full justify-start text-left"
                      onClick={() => handleSelectBrand(brand.id)}
                    >
                      <div className="flex flex-col items-start w-full">
                        <div className="flex items-center gap-2 w-full">
                          <span className="font-medium truncate">{brand.name}</span>
                          {selectedBrandId === brand.id && isDirty && !isNew && (
                            <span className="text-[10px] rounded bg-amber-100 text-amber-700 px-1 py-0.5">Edited</span>
                          )}
                        </div>
                        {brand.tagline && <span className="text-xs text-gray-500 line-clamp-1">{brand.tagline}</span>}
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Core Information</CardTitle>
                <CardDescription>{isNew ? 'Provide fundamental brand attributes.' : 'Define the brand fundamentals used across campaigns.'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="brand-name">Brand Name *</Label>
                    <Input
                      id="brand-name"
                      value={brandDetail.name}
                      onChange={(event) => setBrandDetail(prev => ({ ...prev, name: event.target.value }))}
                      placeholder="Lantmännen Unibake"
                      disabled={detailLoading || saving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="brand-tagline">Tagline</Label>
                    <Input
                      id="brand-tagline"
                      value={brandDetail.tagline ?? ''}
                      onChange={(event) => setBrandDetail(prev => ({ ...prev, tagline: event.target.value }))}
                      placeholder="Baked for growth."
                      disabled={detailLoading || saving}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brand-logo">Logo URL</Label>
                  <Input
                    id="brand-logo"
                    value={brandDetail.logoUrl ?? ''}
                    onChange={(event) => setBrandDetail(prev => ({ ...prev, logoUrl: event.target.value }))}
                    placeholder="https://cdn.example.com/logo.svg"
                    disabled={detailLoading || saving}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Promise</Label>
                  <MarkdownEditor
                    value={brandDetail.promise ?? ''}
                    onChange={(value) => setBrandDetail(prev => ({ ...prev, promise: value }))}
                    placeholder="Delivering oven-warm quality at scale."
                    disabled={detailLoading || saving}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Value Proposition</Label>
                  <MarkdownEditor
                    value={brandDetail.valueProposition ?? ''}
                    onChange={(value) => setBrandDetail(prev => ({ ...prev, valueProposition: value }))}
                    placeholder="Artisanal taste with industrial consistency."
                    disabled={detailLoading || saving}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Naming & Values</CardTitle>
                <CardDescription>Localized variants and brand values.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900">Name Variants</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setBrandDetail(prev => ({
                          ...prev,
                          nameVariants: [...prev.nameVariants, { locale: '', shortName: '', longName: '', phonetic: '' }]
                        }))
                      }
                      disabled={detailLoading || saving}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Variant
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {brandDetail.nameVariants.length === 0 && (
                      <p className="text-sm text-gray-500">No name variants added yet.</p>
                    )}
                    {brandDetail.nameVariants.map((variant, index) => (
                      <Card key={index} className="border-gray-200">
                        <CardContent className="pt-4 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div className="space-y-1">
                              <Label>Locale</Label>
                              <Input
                                value={variant.locale}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setBrandDetail(prev => {
                                    const variants = [...prev.nameVariants];
                                    variants[index] = { ...variants[index], locale: value };
                                    return { ...prev, nameVariants: variants };
                                  });
                                }}
                                placeholder="en-GB"
                                disabled={detailLoading || saving}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>Short Name</Label>
                              <Input
                                value={variant.shortName}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setBrandDetail(prev => {
                                    const variants = [...prev.nameVariants];
                                    variants[index] = { ...variants[index], shortName: value };
                                    return { ...prev, nameVariants: variants };
                                  });
                                }}
                                placeholder="Unibake"
                                disabled={detailLoading || saving}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>Long Name</Label>
                              <Input
                                value={variant.longName ?? ''}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setBrandDetail(prev => {
                                    const variants = [...prev.nameVariants];
                                    variants[index] = { ...variants[index], longName: value };
                                    return { ...prev, nameVariants: variants };
                                  });
                                }}
                                placeholder="Lantmännen Unibake"
                                disabled={detailLoading || saving}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>Phonetic</Label>
                              <Input
                                value={variant.phonetic ?? ''}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setBrandDetail(prev => {
                                    const variants = [...prev.nameVariants];
                                    variants[index] = { ...variants[index], phonetic: value };
                                    return { ...prev, nameVariants: variants };
                                  });
                                }}
                                placeholder="LANT-men-en YOON-ee-bake"
                                disabled={detailLoading || saving}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() =>
                                setBrandDetail(prev => {
                                  const variants = [...prev.nameVariants];
                                  variants.splice(index, 1);
                                  return { ...prev, nameVariants: variants };
                                })
                              }
                              disabled={detailLoading || saving}
                            >
                              <Trash2 className="w-4 h-4 mr-1" /> Remove
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Values</Label>
                      <Button variant="outline" size="sm" onClick={() => addStringToField('values')} disabled={detailLoading || saving}>
                        <Plus className="w-4 h-4 mr-1" /> Add Value
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.values.length === 0 && <p className="text-sm text-gray-500">No values added.</p>}
                      {brandDetail.values.map((value, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={value}
                            onChange={(event) => updateStringArrayField('values', index, event.target.value)}
                            placeholder="Sustainability"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeStringFromField('values', index)}
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Differentiators</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setBrandDetail(prev => ({
                            ...prev,
                            differentiators: [...prev.differentiators, { text: '', order: prev.differentiators.length + 1 }]
                          }))
                        }
                        disabled={detailLoading || saving}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Differentiator
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.differentiators.length === 0 && <p className="text-sm text-gray-500">No differentiators yet.</p>}
                      {brandDetail.differentiators.map((differentiator, index) => (
                        <div key={index} className="grid grid-cols-[1fr_100px_36px] gap-2">
                          <Input
                            value={differentiator.text}
                            onChange={(event) => handleDifferentiatorChange(index, 'text', event.target.value)}
                            placeholder="Pan-European logistics"
                            disabled={detailLoading || saving}
                          />
                          <Input
                            type="number"
                            value={differentiator.order ?? 1}
                            onChange={(event) => handleDifferentiatorChange(index, 'order', event.target.value)}
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() =>
                              setBrandDetail(prev => {
                                const differentiators = [...prev.differentiators];
                                differentiators.splice(index, 1);
                                return { ...prev, differentiators };
                              })
                            }
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Tone & Voice</CardTitle>
                <CardDescription>Define how the brand communicates.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Archetype</Label>
                    <Input
                      value={brandDetail.tone.archetype ?? ''}
                      onChange={(event) => setBrandDetail(prev => ({ ...prev, tone: { ...prev.tone, archetype: event.target.value } }))}
                      placeholder="Caregiver"
                      disabled={detailLoading || saving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Style Notes</Label>
                    <MarkdownEditor
                      value={brandDetail.tone.styleNotes ?? ''}
                      onChange={(value) => setBrandDetail(prev => ({ ...prev, tone: { ...prev.tone, styleNotes: value } }))}
                      placeholder="Short, clear sentences. No slang."
                      disabled={detailLoading || saving}
                      minHeight={140}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Do Keywords</Label>
                      <Button variant="outline" size="sm" onClick={() => addToneKeyword('doKeywords')} disabled={detailLoading || saving}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.tone.doKeywords.length === 0 && <p className="text-sm text-gray-500">Add preferred tone descriptors.</p>}
                      {brandDetail.tone.doKeywords.map((keyword, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={keyword}
                            onChange={(event) => handleToneKeywordChange('doKeywords', index, event.target.value)}
                            placeholder="Friendly"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeToneKeyword('doKeywords', index)}
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Don't Keywords</Label>
                      <Button variant="outline" size="sm" onClick={() => addToneKeyword('dontKeywords')} disabled={detailLoading || saving}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.tone.dontKeywords.length === 0 && <p className="text-sm text-gray-500">Add tone guardrails.</p>}
                      {brandDetail.tone.dontKeywords.map((keyword, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={keyword}
                            onChange={(event) => handleToneKeywordChange('dontKeywords', index, event.target.value)}
                            placeholder="Arrogant"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeToneKeyword('dontKeywords', index)}
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Tone Examples</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setBrandDetail(prev => ({
                          ...prev,
                          tone: {
                            ...prev.tone,
                            examples: [...prev.tone.examples, { context: '', exampleText: '', order: prev.tone.examples.length + 1 }]
                          }
                        }))
                      }
                      disabled={detailLoading || saving}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Example
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {brandDetail.tone.examples.length === 0 && <p className="text-sm text-gray-500">No tone examples yet.</p>}
                    {brandDetail.tone.examples.map((example, index) => (
                      <Card key={index} className="border-gray-200">
                        <CardContent className="pt-4 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-3">
                            <div className="space-y-1">
                              <Label>Context</Label>
                              <Input
                                value={example.context ?? ''}
                                onChange={(event) => handleToneExampleChange(index, 'context', event.target.value)}
                                placeholder="LinkedIn thought leadership"
                                disabled={detailLoading || saving}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>Order</Label>
                              <Input
                                type="number"
                                value={example.order ?? 1}
                                onChange={(event) => handleToneExampleChange(index, 'order', event.target.value)}
                                disabled={detailLoading || saving}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label>Example Text</Label>
                            <MarkdownEditor
                              value={example.exampleText}
                              onChange={(value) => handleToneExampleChange(index, 'exampleText', value)}
                              placeholder="Consistency is the quiet engine behind growth…"
                              disabled={detailLoading || saving}
                              minHeight={140}
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() =>
                                setBrandDetail(prev => ({
                                  ...prev,
                                  tone: {
                                    ...prev.tone,
                                    examples: prev.tone.examples.filter((_, exampleIndex) => exampleIndex !== index)
                                  }
                                }))
                              }
                              disabled={detailLoading || saving}
                            >
                              <Trash2 className="w-4 h-4 mr-1" /> Remove
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Messaging Pillars</CardTitle>
                <CardDescription>Key pillars with supporting messages.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-900">Pillars</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setBrandDetail(prev => ({
                        ...prev,
                        pillars: [...prev.pillars, { name: '', priority: prev.pillars.length + 1, messages: [] }]
                      }))
                    }
                    disabled={detailLoading || saving}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Pillar
                  </Button>
                </div>
                <div className="space-y-3">
                  {brandDetail.pillars.length === 0 && <p className="text-sm text-gray-500">No pillars defined yet.</p>}
                  {brandDetail.pillars.map((pillar, pillarIndex) => (
                    <Card key={pillarIndex} className="border-gray-200">
                      <CardContent className="pt-4 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_40px] gap-3">
                          <Input
                            value={pillar.name}
                            onChange={(event) => handlePillarChange(pillarIndex, 'name', event.target.value)}
                            placeholder="Quality"
                            disabled={detailLoading || saving}
                          />
                          <Input
                            type="number"
                            value={pillar.priority ?? ''}
                            onChange={(event) => handlePillarChange(pillarIndex, 'priority', event.target.value)}
                            placeholder="Priority"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() =>
                              setBrandDetail(prev => ({
                                ...prev,
                                pillars: prev.pillars.filter((_, index) => index !== pillarIndex)
                              }))
                            }
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Key Messages</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setBrandDetail(prev => {
                                  const pillars = [...prev.pillars];
                                  const pillarToUpdate = pillars[pillarIndex];
                                  pillars[pillarIndex] = {
                                    ...pillarToUpdate,
                                    messages: [...pillarToUpdate.messages, { message: '', order: pillarToUpdate.messages.length + 1 }]
                                  };
                                  return { ...prev, pillars };
                                })
                              }
                              disabled={detailLoading || saving}
                            >
                              <Plus className="w-4 h-4 mr-1" /> Add Message
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {pillar.messages.length === 0 && <p className="text-sm text-gray-500">No messages defined.</p>}
                            {pillar.messages.map((message, messageIndex) => (
                              <div key={messageIndex} className="grid grid-cols-1 md:grid-cols-[1fr_120px_36px] gap-2">
                                <MarkdownEditor
                                  value={message.message}
                                  onChange={(value) => handlePillarMessageChange(pillarIndex, messageIndex, 'message', value)}
                                  placeholder="Oven-warm quality"
                                  disabled={detailLoading || saving}
                                  minHeight={120}
                                />
                                <Input
                                  type="number"
                                  value={message.order ?? 1}
                                  onChange={(event) => handlePillarMessageChange(pillarIndex, messageIndex, 'order', event.target.value)}
                                  disabled={detailLoading || saving}
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() =>
                                    setBrandDetail(prev => {
                                      const pillars = [...prev.pillars];
                                      const pillarToUpdate = pillars[pillarIndex];
                                      const messages = pillarToUpdate.messages.filter((_, idx) => idx !== messageIndex);
                                      pillars[pillarIndex] = { ...pillarToUpdate, messages };
                                      return { ...prev, pillars };
                                    })
                                  }
                                  disabled={detailLoading || saving}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Phrases & Storytelling</CardTitle>
                <CardDescription>Guardrails for phrasing and storytelling.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Approved Phrases</Label>
                      <Button variant="outline" size="sm" onClick={() => addStringToField('approvedPhrases')} disabled={detailLoading || saving}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.approvedPhrases.length === 0 && <p className="text-sm text-gray-500">No approved phrases.</p>}
                      {brandDetail.approvedPhrases.map((phrase, index) => (
                        <div key={index} className="flex gap-2">
                          <Textarea
                            value={phrase}
                            onChange={(event) => updateStringArrayField('approvedPhrases', index, event.target.value)}
                            placeholder="Better food. Better planet."
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeStringFromField('approvedPhrases', index)}
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Banned Phrases</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setBrandDetail(prev => ({
                            ...prev,
                            bannedPhrases: [...prev.bannedPhrases, { phrase: '', reason: '' }]
                          }))
                        }
                        disabled={detailLoading || saving}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.bannedPhrases.length === 0 && <p className="text-sm text-gray-500">No banned phrases defined.</p>}
                      {brandDetail.bannedPhrases.map((phrase, index) => (
                        <div key={index} className="space-y-2 border rounded-md p-3">
                          <Input
                            value={phrase.phrase}
                            onChange={(event) => handleBannedPhraseChange(index, 'phrase', event.target.value)}
                            placeholder="Cheap"
                            disabled={detailLoading || saving}
                          />
                          <Textarea
                            value={phrase.reason ?? ''}
                            onChange={(event) => handleBannedPhraseChange(index, 'reason', event.target.value)}
                            placeholder="Does not reflect premium positioning"
                            disabled={detailLoading || saving}
                          />
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() =>
                                setBrandDetail(prev => ({
                                  ...prev,
                                  bannedPhrases: prev.bannedPhrases.filter((_, idx) => idx !== index)
                                }))
                              }
                              disabled={detailLoading || saving}
                            >
                              <Trash2 className="w-4 h-4 mr-1" /> Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Storylines</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setBrandDetail(prev => ({
                          ...prev,
                          storylines: [...prev.storylines, { title: '', order: prev.storylines.length + 1 }]
                        }))
                      }
                      disabled={detailLoading || saving}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Storyline
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {brandDetail.storylines.length === 0 && <p className="text-sm text-gray-500">No storylines defined.</p>}
                    {brandDetail.storylines.map((storyline, index) => (
                      <div key={index} className="grid grid-cols-[1fr_120px_36px] gap-2">
                        <Input
                          value={storyline.title}
                          onChange={(event) => handleStorylineChange(index, 'title', event.target.value)}
                          placeholder="Innovation"
                          disabled={detailLoading || saving}
                        />
                        <Input
                          type="number"
                          value={storyline.order ?? 1}
                          onChange={(event) => handleStorylineChange(index, 'order', event.target.value)}
                          disabled={detailLoading || saving}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() =>
                            setBrandDetail(prev => ({
                              ...prev,
                              storylines: prev.storylines.filter((_, idx) => idx !== index)
                            }))
                          }
                          disabled={detailLoading || saving}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Guardrails</CardTitle>
                <CardDescription>Disclaimers, claims, and sensitivities.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Disclaimers</Label>
                    <Button variant="outline" size="sm" onClick={() => addStringToField('disclaimers')} disabled={detailLoading || saving}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {brandDetail.disclaimers.length === 0 && <p className="text-sm text-gray-500">No disclaimers defined.</p>}
                    {brandDetail.disclaimers.map((disclaimer, index) => (
                      <div key={index} className="flex gap-2">
                        <MarkdownEditor
                          value={disclaimer}
                          onChange={(value) => updateStringArrayField('disclaimers', index, value)}
                          placeholder="No artificial preservatives."
                          disabled={detailLoading || saving}
                          minHeight={120}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => removeStringFromField('disclaimers', index)}
                          disabled={detailLoading || saving}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Claims</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setBrandDetail(prev => ({
                          ...prev,
                          claims: [...prev.claims, { text: '', approved: false, expiryDate: null, jurisdictions: [] }]
                        }))
                      }
                      disabled={detailLoading || saving}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Claim
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {brandDetail.claims.length === 0 && <p className="text-sm text-gray-500">No claims recorded.</p>}
                    {brandDetail.claims.map((claim, index) => (
                      <Card key={index} className="border-gray-200">
                        <CardContent className="pt-4 space-y-3">
                          <div className="space-y-2">
                            <Label>Claim Text</Label>
                            <MarkdownEditor
                              value={claim.text}
                              onChange={(value) => handleClaimChange(index, 'text', value)}
                              placeholder="No palm oil"
                              disabled={detailLoading || saving}
                              minHeight={120}
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-1">
                              <Label>Approved</Label>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={claim.approved}
                                  onCheckedChange={(checked) => handleClaimChange(index, 'approved', checked)}
                                  disabled={detailLoading || saving}
                                />
                                <span className="text-sm text-gray-600">{claim.approved ? 'Approved' : 'Pending'}</span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label>Expiry Date</Label>
                              <Input
                                type="date"
                                value={claim.expiryDate ? claim.expiryDate : ''}
                                onChange={(event) => handleClaimChange(index, 'expiryDate', event.target.value)}
                                disabled={detailLoading || saving}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Jurisdictions</Label>
                              <Button variant="outline" size="sm" onClick={() => addClaimJurisdiction(index)} disabled={detailLoading || saving}>
                                <Plus className="w-4 h-4 mr-1" /> Add
                              </Button>
                            </div>
                            <div className="space-y-2">
                              {claim.jurisdictions.length === 0 && <p className="text-sm text-gray-500">No jurisdictions set.</p>}
                              {claim.jurisdictions.map((jurisdiction, jurisdictionIndex) => (
                                <div key={jurisdictionIndex} className="flex gap-2">
                                  <Input
                                    value={jurisdiction}
                                    onChange={(event) => handleClaimJurisdictionChange(index, jurisdictionIndex, event.target.value)}
                                    placeholder="EU"
                                    disabled={detailLoading || saving}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => removeClaimJurisdiction(index, jurisdictionIndex)}
                                    disabled={detailLoading || saving}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Cultural Sensitivities</Label>
                    <Button variant="outline" size="sm" onClick={() => addStringToField('culturalSensitivities')} disabled={detailLoading || saving}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {brandDetail.culturalSensitivities.length === 0 && <p className="text-sm text-gray-500">No sensitivities noted.</p>}
                    {brandDetail.culturalSensitivities.map((sensitivity, index) => (
                      <div key={index} className="flex gap-2">
                        <Textarea
                          value={sensitivity}
                          onChange={(event) => updateStringArrayField('culturalSensitivities', index, event.target.value)}
                          placeholder="Avoid references to wheat subsidies in DK communications"
                          disabled={detailLoading || saving}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => removeStringFromField('culturalSensitivities', index)}
                          disabled={detailLoading || saving}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Context & Audiences</CardTitle>
                <CardDescription>Target audiences, markets, languages, and topics.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-gray-900">Audiences</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setBrandDetail(prev => ({
                          ...prev,
                          audiences: [...prev.audiences, { targetProfileOptionId: allTargetProfiles[0]?.id ?? 0, description: '', valueDrivers: [], objections: [] }]
                        }))
                      }
                      disabled={detailLoading || saving || allTargetProfiles.length === 0}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Audience
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {brandDetail.audiences.length === 0 && <p className="text-sm text-gray-500">No audiences linked yet.</p>}
                    {brandDetail.audiences.map((audience, index) => (
                      <Card key={index} className="border-gray-200">
                        <CardContent className="pt-4 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-3">
                            <div className="space-y-1">
                              <Label>Target Profile</Label>
                              <Select
                                value={audience.targetProfileOptionId ? audience.targetProfileOptionId.toString() : ''}
                                onValueChange={(value) => handleAudienceChange(index, 'targetProfileOptionId', value)}
                                disabled={detailLoading || saving || allTargetProfiles.length === 0}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={allTargetProfiles.length === 0 ? 'No profiles available' : 'Select profile'} />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                  {allTargetProfiles.map(profile => (
                                    <SelectItem key={profile.id} value={profile.id.toString()}>
                                      {profile.name} ({profile.audience})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700"
                                onClick={() =>
                                  setBrandDetail(prev => ({
                                    ...prev,
                                    audiences: prev.audiences.filter((_, idx) => idx !== index)
                                  }))
                                }
                                disabled={detailLoading || saving}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <Label>Description</Label>
                            <Textarea
                              value={audience.description ?? ''}
                              onChange={(event) => handleAudienceChange(index, 'description', event.target.value)}
                              placeholder="Foodservice buyers seeking consistent quality"
                              disabled={detailLoading || saving}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label>Value Drivers</Label>
                                <Button variant="outline" size="sm" onClick={() => addAudienceEntry(index, 'valueDrivers')} disabled={detailLoading || saving}>
                                  <Plus className="w-4 h-4 mr-1" /> Add
                                </Button>
                              </div>
                              <div className="space-y-2">
                                {audience.valueDrivers.length === 0 && <p className="text-sm text-gray-500">No value drivers defined.</p>}
                                {audience.valueDrivers.map((driver, driverIndex) => (
                                  <div key={driverIndex} className="flex gap-2">
                                    <Input
                                      value={driver}
                                      onChange={(event) => updateAudienceStringList(index, 'valueDrivers', driverIndex, event.target.value)}
                                      placeholder="Reliability"
                                      disabled={detailLoading || saving}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-red-500 hover:text-red-700"
                                      onClick={() => removeAudienceEntry(index, 'valueDrivers', driverIndex)}
                                      disabled={detailLoading || saving}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label>Objections</Label>
                                <Button variant="outline" size="sm" onClick={() => addAudienceEntry(index, 'objections')} disabled={detailLoading || saving}>
                                  <Plus className="w-4 h-4 mr-1" /> Add
                                </Button>
                              </div>
                              <div className="space-y-2">
                                {audience.objections.length === 0 && <p className="text-sm text-gray-500">No objections captured.</p>}
                                {audience.objections.map((objection, objectionIndex) => (
                                  <div key={objectionIndex} className="flex gap-2">
                                    <Input
                                      value={objection}
                                      onChange={(event) => updateAudienceStringList(index, 'objections', objectionIndex, event.target.value)}
                                      placeholder="Price"
                                      disabled={detailLoading || saving}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-red-500 hover:text-red-700"
                                      onClick={() => removeAudienceEntry(index, 'objections', objectionIndex)}
                                      disabled={detailLoading || saving}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Markets</Label>
                      <Button variant="outline" size="sm" onClick={() => addStringToField('markets')} disabled={detailLoading || saving}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.markets.length === 0 && <p className="text-sm text-gray-500">No markets selected.</p>}
                      {brandDetail.markets.map((market, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={market}
                            onChange={(event) => updateStringArrayField('markets', index, event.target.value)}
                            placeholder="SE"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeStringFromField('markets', index)}
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Languages</Label>
                      <Button variant="outline" size="sm" onClick={() => addStringToField('languages')} disabled={detailLoading || saving}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.languages.length === 0 && <p className="text-sm text-gray-500">No languages listed.</p>}
                      {brandDetail.languages.map((language, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={language}
                            onChange={(event) => updateStringArrayField('languages', index, event.target.value)}
                            placeholder="en"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeStringFromField('languages', index)}
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Topics</Label>
                      <Button variant="outline" size="sm" onClick={() => addStringToField('topics')} disabled={detailLoading || saving}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.topics.length === 0 && <p className="text-sm text-gray-500">No topics defined.</p>}
                      {brandDetail.topics.map((topic, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={topic}
                            onChange={(event) => updateStringArrayField('topics', index, event.target.value)}
                            placeholder="bakery"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeStringFromField('topics', index)}
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Hashtags</Label>
                      <Button variant="outline" size="sm" onClick={() => addStringToField('hashtags')} disabled={detailLoading || saving}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {brandDetail.hashtags.length === 0 && <p className="text-sm text-gray-500">No hashtags defined.</p>}
                      {brandDetail.hashtags.map((tag, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={tag}
                            onChange={(event) => updateStringArrayField('hashtags', index, event.target.value)}
                            placeholder="#BetterFoodBetterPlanet"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeStringFromField('hashtags', index)}
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Channel Guidance</CardTitle>
                <CardDescription>Tailor tone and CTAs by channel.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-900">Channels</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setBrandDetail(prev => ({
                        ...prev,
                        channels: [...prev.channels, { name: '', toneOverride: '', lengthGuidance: '', callToActions: [] }]
                      }))
                    }
                    disabled={detailLoading || saving}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Channel
                  </Button>
                </div>
                <div className="space-y-3">
                  {brandDetail.channels.length === 0 && <p className="text-sm text-gray-500">No channels configured.</p>}
                  {brandDetail.channels.map((channel, index) => (
                    <Card key={index} className="border-gray-200">
                      <CardContent className="pt-4 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_40px] gap-3">
                          <Input
                            value={channel.name}
                            onChange={(event) => handleChannelChange(index, 'name', event.target.value)}
                            placeholder="LinkedIn"
                            disabled={detailLoading || saving}
                          />
                          <Input
                            value={channel.lengthGuidance ?? ''}
                            onChange={(event) => handleChannelChange(index, 'lengthGuidance', event.target.value)}
                            placeholder="80–140 words"
                            disabled={detailLoading || saving}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() =>
                              setBrandDetail(prev => ({
                                ...prev,
                                channels: prev.channels.filter((_, idx) => idx !== index)
                              }))
                            }
                            disabled={detailLoading || saving}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-1">
                          <Label>Tone Override</Label>
                          <MarkdownEditor
                            value={channel.toneOverride ?? ''}
                            onChange={(value) => handleChannelChange(index, 'toneOverride', value)}
                            placeholder="Professional, B2B focus"
                            disabled={detailLoading || saving}
                            minHeight={120}
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>CTA Presets</Label>
                            <Button variant="outline" size="sm" onClick={() => addChannelCta(index)} disabled={detailLoading || saving}>
                              <Plus className="w-4 h-4 mr-1" /> Add CTA
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {channel.callToActions.length === 0 && <p className="text-sm text-gray-500">No CTAs defined.</p>}
                            {channel.callToActions.map((cta, ctaIndex) => (
                              <div key={ctaIndex} className="flex gap-2">
                                <Input
                                  value={cta}
                                  onChange={(event) => updateChannelCta(index, ctaIndex, event.target.value)}
                                  placeholder="Learn more"
                                  disabled={detailLoading || saving}
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => removeChannelCta(index, ctaIndex)}
                                  disabled={detailLoading || saving}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Spacer card to always show at least one empty card for adding new channel */}
                  <Card className="border-dashed border-gray-300">
                    <CardContent className="flex items-center justify-center h-20">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setBrandDetail(prev => ({
                            ...prev,
                            channels: [...prev.channels, { name: '', toneOverride: '', lengthGuidance: '', callToActions: [] }]
                          }))
                        }
                        disabled={detailLoading || saving}
                        className="w-full h-full"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Channel
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
